import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";

const BUDGETS = [
  { v: "₹", label: "Budget (₹)" },
  { v: "₹₹", label: "Mid-range (₹₹)" },
  { v: "₹₹₹", label: "Luxury (₹₹₹)" },
];
const TRIP_TYPES = ["Solo", "Couple", "Family", "Group"];
const SEASONS = ["Spring", "Summer", "Autumn", "Winter"];
const QUICK_PRICES = [0, 99, 199, 499, 999];

const emptyBlocks = () => ["morning", "afternoon", "evening"].map((tod, i) => ({
  block_id: null,
  time_of_day: tod,
  activity_name: "",
  description: "",
  location_name: "",
  creator_tip: "",
  approx_cost: "",
  photo_url: null,
  sort_order: i,
}));

const emptyDay = (n) => ({
  day_id: null,
  day_number: n,
  day_title: "",
  blocks: emptyBlocks(),
});

const emptyItin = () => ({
  itinerary_id: null,
  slug: null,
  status: "draft",
  title: "",
  description: "",
  cover_url: null,
  destination_country: "",
  destination_city: "",
  duration_days: 1,
  budget_range: "$$",
  trip_type: [],
  best_season: [],
  visa_notes: "",
  estimated_cost_usd: "",
  price_usd: 0,
  days: [emptyDay(1)],
  extras: { restaurants: "", accommodation: "", packing_list: "", getting_around: "", emergency_contacts: "" },
});

const stripDoc = (d) => ({
  title: d.title || "",
  description: d.description || "",
  cover_url: d.cover_url || null,
  destination_country: d.destination_country || "",
  destination_city: d.destination_city || "",
  region: d.region || "",
  duration_days: d.duration_days || 1,
  budget_range: d.budget_range || "$$",
  trip_type: d.trip_type || [],
  best_season: d.best_season || [],
  visa_notes: d.visa_notes || "",
  estimated_cost_usd: d.estimated_cost_usd || "",
  price_usd: Number(d.price_usd) || 0,
  days: (d.days || []).map((day) => ({
    day_id: day.day_id || null,
    day_number: day.day_number,
    day_title: day.day_title || "",
    blocks: (day.blocks || []).map((b, i) => ({
      block_id: b.block_id || null,
      time_of_day: b.time_of_day,
      activity_name: b.activity_name || "",
      description: b.description || "",
      location_name: b.location_name || "",
      creator_tip: b.creator_tip || "",
      approx_cost: b.approx_cost || "",
      photo_url: b.photo_url || null,
      sort_order: i,
    })),
  })),
  extras: d.extras || { restaurants: "", accommodation: "", packing_list: "", getting_around: "", emergency_contacts: "" },
});

const validateLocally = (d) => {
  const errs = [];
  if (!d.title?.trim()) errs.push("Title");
  if (!d.cover_url) errs.push("Cover photo");
  if (!d.description?.trim()) errs.push("Description");
  if (!d.destination_country?.trim() || !d.destination_city?.trim()) errs.push("Destination");
  let full = 0;
  for (const day of d.days || []) {
    const slots = {};
    for (const b of day.blocks || []) slots[b.time_of_day] = b;
    if (["morning", "afternoon", "evening"].every((t) => slots[t] && slots[t].activity_name?.trim() && slots[t].description?.trim())) {
      full += 1;
    }
  }
  if (full < 2) errs.push(`At least 2 complete days (${full}/2 done)`);
  if (d.price_usd === undefined || d.price_usd === null || isNaN(d.price_usd)) errs.push("Price");
  return errs;
};

export default function Builder() {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(editId);

  const [itin, setItin] = useState(() => emptyItin());
  const [activeSection, setActiveSection] = useState("basics");
  const [loading, setLoading] = useState(isEditing);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const dirtyRef = useRef(false);
  const saveTimer = useRef(null);

  // Initial load (edit) or create new draft
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isEditing) {
        try {
          const r = await api.get(`/seller/itineraries/${editId}`);
          if (!cancelled) {
            const d = r.data;
            // Guard against nullable Supabase responses.
            d.days = d.days ?? [];
            d.trip_type = d.trip_type ?? [];
            d.best_season = d.best_season ?? [];
            d.extras = d.extras ?? { restaurants: "", accommodation: "", packing_list: "", getting_around: "", emergency_contacts: "" };
            // Pad to duration_days
            while (d.days.length < d.duration_days) d.days.push(emptyDay(d.days.length + 1));
            setItin(d);
            setLoading(false);
          }
        } catch {
          toast.error("Could not load itinerary");
          navigate("/seller", { replace: true });
        }
      } else {
        try {
          const r = await api.post("/seller/itineraries", { title: "", duration_days: 1, days: [{ day_number: 1, day_title: "", blocks: emptyBlocks() }] });
          if (!cancelled) {
            navigate(`/seller/edit/${r.data.itinerary_id}`, { replace: true });
          }
        } catch {
          toast.error("Could not create draft");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [editId, isEditing, navigate]);

  // Auto-save debounced after user pauses
  const persistNow = useCallback(async (data) => {
    if (!data.itinerary_id) return;
    setSaveStatus("saving");
    try {
      const r = await api.put(`/seller/itineraries/${data.itinerary_id}`, stripDoc(data));
      dirtyRef.current = false;
      setLastSavedAt(Date.now());
      setSaveStatus("saved");
      // Update returned slug etc.
      setItin((cur) => (cur.itinerary_id === r.data.itinerary_id ? { ...cur, slug: r.data.slug, status: r.data.status } : cur));
    } catch {
      setSaveStatus("error");
    }
  }, []);

  useEffect(() => {
    if (!itin.itinerary_id) return;
    dirtyRef.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistNow(itin), 1200);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [itin, persistNow]);

  // Periodic 30s safety net
  useEffect(() => {
    const id = setInterval(() => {
      if (dirtyRef.current && itin.itinerary_id) persistNow(itin);
    }, 30000);
    return () => clearInterval(id);
  }, [itin, persistNow]);

  // Warn on exit if unsaved
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Adjust days array when duration_days changes
  const setDuration = (n) => {
    n = Math.max(1, Math.min(30, n));
    setItin((d) => {
      const days = [...d.days];
      while (days.length < n) days.push(emptyDay(days.length + 1));
      while (days.length > n) days.pop();
      return { ...d, duration_days: n, days };
    });
  };

  const update = (patch) => setItin((d) => ({ ...d, ...patch }));
  const updateExtras = (patch) => setItin((d) => ({ ...d, extras: { ...d.extras, ...patch } }));
  const updateDay = (idx, patch) => setItin((d) => {
    const days = [...d.days];
    days[idx] = { ...days[idx], ...patch };
    return { ...d, days };
  });
  const updateBlock = (dayIdx, blockIdx, patch) => setItin((d) => {
    const days = [...d.days];
    const blocks = [...days[dayIdx].blocks];
    blocks[blockIdx] = { ...blocks[blockIdx], ...patch };
    days[dayIdx] = { ...days[dayIdx], blocks };
    return { ...d, days };
  });
  const toggleArr = (key, v) => setItin((d) => ({
    ...d,
    [key]: d[key].includes(v) ? d[key].filter((x) => x !== v) : [...d[key], v],
  }));

  const handleUpload = async (file, onUrl) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/seller/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUrl(r.data.url);
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const issues = useMemo(() => validateLocally(itin), [itin]);
  const canPublish = issues.length === 0;

  const handlePublish = async () => {
    if (saveStatus === "saving") await persistNow(itin);
    else if (dirtyRef.current) await persistNow(itin);
    try {
      const r = await api.post(`/seller/itineraries/${itin.itinerary_id}/publish`);
      toast.success("Your itinerary is live!");
      navigate("/seller", { state: { just_published: r.data.slug } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.issues?.join(", ") || "Publish failed";
      toast.error(msg);
    }
  };

  const handleUnpublish = async () => {
    try {
      await api.post(`/seller/itineraries/${itin.itinerary_id}/unpublish`);
      setItin((d) => ({ ...d, status: "draft" }));
      toast.success("Itinerary unpublished.");
    } catch { toast.error("Could not unpublish"); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/seller/itineraries/${itin.itinerary_id}`);
      toast.success("Deleted.");
      navigate("/seller");
    } catch { toast.error("Could not delete"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink3)]">◆ Loading…</div>
      </div>
    );
  }

  const sections = [
    { id: "basics", label: "Basics" },
    ...(itin.days ?? []).map((d) => ({ id: `day-${d.day_number}`, label: `Day ${d.day_number}` })),
    { id: "extras", label: "Extras" },
    { id: "pricing", label: "Pricing" },
  ];

  const savedLabel = saveStatus === "saving"
    ? "Saving…"
    : lastSavedAt
      ? `Saved ${ago(lastSavedAt)}`
      : "Not saved yet";

  return (
    <div className="min-h-screen bg-[var(--paper)] flex flex-col" data-testid="builder">
      {/* TOP BAR */}
      <header className="border-b border-[var(--line)] bg-[var(--paper)]/95 backdrop-blur-md sticky top-0 z-40">
        <div className="px-6 sm:px-8 py-4 flex items-center gap-4">
          <Logo size={22} />
          <Link to="/seller" className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink2)] hover:text-[var(--red)] transition-colors hidden sm:inline">
            ← Dashboard
          </Link>
          <div className="flex-1 min-w-0 px-4">
            <input
              data-testid="builder-title"
              value={itin.title}
              onChange={(e) => update({ title: e.target.value.slice(0, 80) })}
              placeholder="Untitled itinerary"
              className="w-full bg-transparent serif text-[22px] sm:text-[28px] leading-tight outline-none placeholder:text-[var(--ink3)]"
            />
            <div className="mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink3)] mt-1 flex items-center gap-3">
              <span data-testid="save-status">{savedLabel}</span>
              {itin.status === "live" && <span className="text-[var(--green)]">◆ Live</span>}
              {itin.status === "draft" && <span>◆ Draft</span>}
            </div>
          </div>
          <button
            onClick={() => setShowPreview((s) => !s)}
            data-testid="toggle-preview"
            className="btn btn-ghost btn-sm lg:hidden"
          >
            {showPreview ? "Edit" : "Preview"}
          </button>
          {itin.status === "live" ? (
            <button onClick={handleUnpublish} data-testid="unpublish-btn" className="btn btn-paper btn-sm">Unpublish</button>
          ) : null}
          <button
            onClick={handlePublish}
            disabled={!canPublish}
            data-testid="publish-btn"
            className="btn btn-solid btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
            title={canPublish ? "Publish" : `Missing: ${issues.join(", ")}`}
          >
            {itin.status === "live" ? "Update" : "Publish →"}
          </button>
        </div>
      </header>

      {/* CONTENT */}
      <div className="flex-1 grid lg:grid-cols-[270px_1fr_310px] gap-0">
        {/* LEFT NAV */}
        <aside className={`border-r border-[var(--line)] p-5 lg:sticky lg:top-[88px] lg:self-start ${showPreview ? "hidden lg:block" : ""}`}>
          <div className="lg:flex flex-col gap-1.5 grid grid-flow-col auto-cols-max gap-x-2 lg:auto-cols-auto overflow-x-auto lg:overflow-visible" data-testid="section-nav">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => { setActiveSection(s.id); setShowPreview(false); }}
                data-testid={`nav-${s.id}`}
                className={`text-left px-3 py-2 rounded-[10px] text-[13px] font-semibold transition-colors whitespace-nowrap ${activeSection === s.id ? "bg-[var(--red)] text-white" : "text-[var(--ink2)] hover:bg-[var(--line)]/60"}`}
              >{s.label}</button>
            ))}
            <button
              onClick={() => setDuration(itin.duration_days + 1)}
              data-testid="add-day"
              className="text-left px-3 py-2 rounded-[10px] text-[12px] mono uppercase tracking-[0.15em] text-[var(--ink3)] hover:text-[var(--red)] transition-colors mt-2"
            >＋ Add day</button>
          </div>
          <div className="mt-6 hidden lg:block bg-[var(--card)] border border-[var(--line)] rounded-[14px] p-4 text-[12px] leading-[1.55] text-[var(--ink2)]">
            <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--red)] mb-2">◆ Publish checklist</div>
            <ul className="space-y-1">
              {[
                ["Title", itin.title?.trim()],
                ["Cover photo", itin.cover_url],
                ["Description", itin.description?.trim()],
                ["Destination", itin.destination_country?.trim() && itin.destination_city?.trim()],
                ["Price set", itin.price_usd >= 0],
                ["≥ 2 full days", validateLocally(itin).every((x) => !x.startsWith("At least"))],
              ].map(([label, ok]) => (
                <li key={label} className="flex gap-2 items-start">
                  <span className={ok ? "text-[var(--green)]" : "text-[var(--ink3)]"}>{ok ? "✓" : "○"}</span>
                  <span className={ok ? "text-[var(--ink2)]" : "text-[var(--ink3)]"}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
          {confirmDelete ? (
            <div className="mt-4 bg-[#FCEAEC] border border-[var(--red)]/40 rounded-[14px] p-3 text-[12px]">
              <div className="font-bold text-[var(--red)] mb-2">Delete this draft?</div>
              <div className="flex gap-2">
                <button onClick={handleDelete} data-testid="confirm-delete" className="btn btn-solid btn-sm">Yes, delete</button>
                <button onClick={() => setConfirmDelete(false)} className="btn btn-ghost btn-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setConfirmDelete(true)} data-testid="delete-btn" className="mt-4 mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink3)] hover:text-[var(--red)] transition-colors">
              ◆ Delete draft
            </button>
          )}
        </aside>

        {/* CENTER FORM */}
        <main className={`px-6 sm:px-10 py-10 max-w-[760px] w-full mx-auto ${showPreview ? "hidden lg:block" : ""}`} data-testid="builder-form">
          {activeSection === "basics" && (
            <BasicsSection itin={itin} update={update} setDuration={setDuration} toggleArr={toggleArr} handleUpload={handleUpload} />
          )}
          {sections.filter((s) => s.id.startsWith("day-")).map((s, i) => activeSection === s.id && (
            <DaySection key={s.id} day={itin.days[i]} idx={i} updateDay={updateDay} updateBlock={updateBlock} handleUpload={handleUpload} />
          ))}
          {activeSection === "extras" && (
            <ExtrasSection extras={itin.extras} updateExtras={updateExtras} />
          )}
          {activeSection === "pricing" && (
            <PricingSection itin={itin} update={update} />
          )}
        </main>

        {/* RIGHT PREVIEW */}
        <aside className={`border-l border-[var(--line)] p-6 lg:sticky lg:top-[88px] lg:self-start lg:h-[calc(100vh-88px)] lg:overflow-y-auto ${showPreview ? "" : "hidden lg:block"}`} data-testid="live-preview">
          <div className="mono text-[10px] tracking-[0.18em] uppercase text-[var(--red)] mb-3">◆ This is how buyers will see it</div>
          <PreviewCard itin={itin} />
        </aside>
      </div>

      {/* BOTTOM BAR */}
      <footer className="border-t border-[var(--line)] px-6 sm:px-8 py-3 flex items-center justify-between bg-[var(--paper)]/85 backdrop-blur-md">
        <div className="mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink3)]">{savedLabel}</div>
        <button
          onClick={() => persistNow(itin)}
          data-testid="manual-save"
          className="btn btn-paper btn-sm"
        >Save draft</button>
      </footer>
    </div>
  );
}

function ago(ts) {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return "just now";
  if (m === 1) return "1 min ago";
  if (m < 60) return `${m} mins ago`;
  return `${Math.floor(m / 60)}h ago`;
}

// ───────────────── Sections ─────────────────
function CoverUploader({ url, onUpload }) {
  const inputRef = useRef(null);
  return (
    <div
      className="aspect-[16/8] rounded-[16px] border-[1.5px] border-dashed border-[var(--line)] bg-cover bg-center grid place-items-center cursor-pointer hover:border-[var(--ink)] transition-colors relative overflow-hidden"
      style={url ? { backgroundImage: `url(${url})` } : {}}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onUpload(f); }}
      data-testid="cover-upload"
    >
      {!url && (
        <div className="text-center text-[var(--ink3)]">
          <div className="serif text-[40px] text-[var(--red)]">↑</div>
          <div className="mono text-[10px] tracking-[0.18em] uppercase mt-2">Drop photo or click to upload</div>
        </div>
      )}
      {url && (
        <button
          onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          className="btn btn-paper btn-sm absolute bottom-3 right-3"
        >Replace</button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }}
      />
    </div>
  );
}

function BasicsSection({ itin, update, setDuration, toggleArr, handleUpload }) {
  return (
    <div className="space-y-6">
      <span className="eyebrow">◆ 1 — Basics</span>
      <CoverUploader url={itin.cover_url} onUpload={(file) => handleUpload(file, (url) => update({ cover_url: url }))} />

      <Field label={`Title · ${(itin.title ?? '').length}/80`}>
        <input className="inp" data-testid="basics-title" value={itin.title ?? ''} onChange={(e) => update({ title: e.target.value.slice(0, 80) })} placeholder="The Slow Train Through Kyoto" />
      </Field>
      <Field label={`Description · ${(itin.description ?? '').length}/200`}>
        <textarea className="inp min-h-[88px] resize-y" data-testid="basics-desc" value={itin.description ?? ''} onChange={(e) => update({ description: e.target.value.slice(0, 200) })} placeholder="Beyond the tourist trail — what makes this route different?" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Country">
          <input className="inp" data-testid="basics-country" value={itin.destination_country ?? ''} onChange={(e) => update({ destination_country: e.target.value })} placeholder="Japan" />
        </Field>
        <Field label="City">
          <input className="inp" data-testid="basics-city" value={itin.destination_city ?? ''} onChange={(e) => update({ destination_city: e.target.value })} placeholder="Kyoto" />
        </Field>
      </div>
      <Field label={`Duration · ${itin.duration_days} ${itin.duration_days === 1 ? "day" : "days"}`}>
        <input
          className="inp"
          data-testid="basics-duration"
          type="number"
          min={1}
          max={30}
          value={itin.duration_days}
          onChange={(e) => setDuration(parseInt(e.target.value || "1", 10))}
        />
      </Field>
      <Field label="Budget range">
        <div className="flex flex-wrap gap-2">
          {BUDGETS.map((b) => (
            <span
              key={b.v}
              onClick={() => update({ budget_range: b.v })}
              data-testid={`budget-${b.v}`}
              className={`pref-chip ${itin.budget_range === b.v ? "sel" : ""}`}
            >{b.label}</span>
          ))}
        </div>
      </Field>
      <Field label="Trip type">
        <div className="flex flex-wrap gap-2">
          {TRIP_TYPES.map((t) => (
            <span
              key={t}
              onClick={() => toggleArr("trip_type", t)}
              data-testid={`trip-${t}`}
              className={`pref-chip ${(itin.trip_type ?? []).includes(t) ? "sel" : ""}`}
            >{t}</span>
          ))}
        </div>
      </Field>
      <Field label="Best season">
        <div className="flex flex-wrap gap-2">
          {SEASONS.map((s) => (
            <span
              key={s}
              onClick={() => toggleArr("best_season", s)}
              data-testid={`season-${s}`}
              className={`pref-chip ${(itin.best_season ?? []).includes(s) ? "sel" : ""}`}
            >{s}</span>
          ))}
        </div>
      </Field>
      <Field label="Visa notes">
        <textarea className="inp min-h-[80px] resize-y" value={itin.visa_notes ?? ''} onChange={(e) => update({ visa_notes: e.target.value })} placeholder="Visa-free for most nationalities…" />
      </Field>
      <Field label="Estimated total cost (INR range)">
        <input className="inp" value={itin.estimated_cost_usd ?? ''} onChange={(e) => update({ estimated_cost_usd: e.target.value })} placeholder="₹40,000 — ₹80,000 INR" />
      </Field>
    </div>
  );
}

function DaySection({ day, idx, updateDay, updateBlock, handleUpload }) {
  return (
    <div className="space-y-6" data-testid={`section-day-${day.day_number}`}>
      <span className="eyebrow">◆ Day {day.day_number}</span>
      <Field label="Day title">
        <input className="inp" value={day.day_title ?? ''} onChange={(e) => updateDay(idx, { day_title: e.target.value })} placeholder="Arrival + old city walk" data-testid={`day-title-${day.day_number}`} />
      </Field>
      <div className="space-y-5">
        {(day.blocks ?? []).map((b, j) => (
          <div key={`${day.day_number}-${j}`} className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[16px] p-5" data-testid={`block-${day.day_number}-${b.time_of_day}`}>
            <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--red)] mb-3">{b.time_of_day}</div>
            <Field label="Activity name">
              <input className="inp" value={b.activity_name ?? ''} onChange={(e) => updateBlock(idx, j, { activity_name: e.target.value })} placeholder="Walk the old quarter" />
            </Field>
            <Field label={`Description · ${(b.description ?? '').length}/300`}>
              <textarea className="inp min-h-[72px] resize-y" value={b.description ?? ''} onChange={(e) => updateBlock(idx, j, { description: e.target.value.slice(0, 300) })} placeholder="Two hours wandering at street level. No itinerary." />
            </Field>
            <Field label="Location">
              <input className="inp" value={b.location_name ?? ''} onChange={(e) => updateBlock(idx, j, { location_name: e.target.value })} placeholder="Gion district, Kyoto" />
            </Field>
            <Field label={`Creator tip · ${(b.creator_tip ?? '').length}/150`}>
              <input className="inp" value={b.creator_tip ?? ''} onChange={(e) => updateBlock(idx, j, { creator_tip: e.target.value.slice(0, 150) })} placeholder="Cafés on quiet streets > tourist plazas" />
            </Field>
            <Field label="Approx cost">
              <input className="inp" value={b.approx_cost ?? ''} onChange={(e) => updateBlock(idx, j, { approx_cost: e.target.value })} placeholder="₹800 / free" />
            </Field>
            <Field label="Photo (optional)">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, (url) => updateBlock(idx, j, { photo_url: url })); }}
                className="text-[12px] mono text-[var(--ink2)]"
              />
              {b.photo_url && <img src={b.photo_url} alt="" className="mt-2 rounded-[10px] max-h-[120px]" />}
            </Field>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExtrasSection({ extras, updateExtras }) {
  const safe = extras ?? {};
  return (
    <div className="space-y-6">
      <span className="eyebrow">◆ Extras</span>
      <p className="text-[14px] text-[var(--ink2)] -mt-3">Optional. Buyers love these.</p>
      {[
        ["restaurants", "Restaurants", "Hand-picked counters worth queuing for…"],
        ["accommodation", "Accommodation", "Two ryokans, one guesthouse, one budget option…"],
        ["packing_list", "Packing list", "IC card, walking shoes, pocket umbrella…"],
        ["getting_around", "Getting around", "IC card covers buses + subway. Skip JR Pass on this route."],
        ["emergency_contacts", "Emergency contacts", "Local emergency numbers + your embassy."],
      ].map(([k, label, ph]) => (
        <Field key={k} label={label}>
          <textarea
            className="inp min-h-[88px] resize-y"
            value={safe[k] ?? ""}
            onChange={(e) => updateExtras({ [k]: e.target.value })}
            placeholder={ph}
            data-testid={`extras-${k}`}
          />
        </Field>
      ))}
    </div>
  );
}

function PricingSection({ itin, update }) {
  const [custom, setCustom] = useState(!QUICK_PRICES.includes(Number(itin.price_usd)));
  return (
    <div className="space-y-6">
      <span className="eyebrow">◆ Pricing</span>
      <Field label="Price (INR)">
        <div className="flex flex-wrap gap-2">
          {QUICK_PRICES.map((p) => (
            <span
              key={p}
              onClick={() => { setCustom(false); update({ price_usd: p }); }}
              data-testid={`price-${p}`}
              className={`pref-chip ${!custom && Number(itin.price_usd) === p ? "sel" : ""}`}
            >{p === 0 ? "Free" : `₹${p}`}</span>
          ))}
          <span
            onClick={() => setCustom(true)}
            data-testid="price-custom"
            className={`pref-chip ${custom ? "sel" : ""}`}
          >Custom</span>
        </div>
        {custom && (
          <input
            className="inp mt-3"
            type="number"
            min={0}
            step={1}
            value={itin.price_usd}
            onChange={(e) => update({ price_usd: parseFloat(e.target.value || "0") })}
            data-testid="price-custom-input"
            placeholder="Enter price in INR"
          />
        )}
      </Field>

      <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[16px] p-5" data-testid="fee-notice">
        <div className="mono text-[10px] tracking-[0.18em] uppercase text-[var(--red)] mb-2">◆ Your earnings</div>
        <p className="text-[14px] leading-[1.65]">
          You keep the full sale price — no commission taken. {itin.price_usd > 0
            ? <>At <strong>₹{Number(itin.price_usd)}</strong> you earn <strong>₹{Number(itin.price_usd)}</strong> per sale.</>
            : <>This route will be free — great for building your audience.</>}
        </p>
      </div>

      <div className="bg-[var(--paper)] border border-dashed border-[var(--line)] rounded-[16px] p-5 text-[13px] leading-[1.65] text-[var(--ink2)]">
        <div className="mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink3)] mb-2">◆ Suggested pricing</div>
        <ul className="space-y-1">
          <li>Short domestic (2–4 days): <strong>₹99 — ₹299</strong></li>
          <li>Medium international (5–8 days): <strong>₹299 — ₹799</strong></li>
          <li>Long / complex (9+ days): <strong>₹499 — ₹1999</strong></li>
          <li>Free: great for building your audience</li>
        </ul>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="lbl">{label}</label>
      {children}
    </div>
  );
}

// ───────────────── Live Preview ─────────────────
function PreviewCard({ itin }) {
  const isFree = itin.price_usd === 0;
  return (
    <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[16px] overflow-hidden">
      <div
        className="aspect-[4/3] bg-cover bg-center bg-[var(--line)]"
        style={itin.cover_url ? { backgroundImage: `url(${itin.cover_url})` } : {}}
      />
      <div className="p-4">
        <div className="mono text-[9px] tracking-[0.15em] uppercase text-[var(--ink3)]">
          {itin.destination_country || "Country"} · {itin.duration_days} Days
        </div>
        <div className="serif text-[18px] mt-1 leading-tight">{itin.title || "Untitled itinerary"}</div>
        <div className="text-[12px] leading-[1.55] text-[var(--ink2)] mt-2">
          {itin.description || "Add a description to see it here."}
        </div>
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {(itin.trip_type ?? []).slice(0, 2).map((t) => <span key={t} className="tag">{t}</span>)}
          <span className="tag">{itin.budget_range}</span>
        </div>
        <div className="mt-4 pt-3 border-t border-dashed border-[var(--line)] flex items-center justify-between">
          <div className="mono text-[10px] uppercase tracking-[0.15em] text-[var(--ink3)]">Day 1 free preview</div>
          <div className={isFree ? "card-price-free" : "card-price"} style={{ fontSize: 22 }}>
            {isFree ? "FREE" : `₹${Number(itin.price_usd)}`}
          </div>
        </div>
      </div>

      {/* Days summary */}
      <div className="px-4 pb-4 space-y-2">
        {(itin.days ?? []).slice(0, 1).map((d) => (
          <div key={d.day_number} className="text-[12px] leading-[1.5]">
            <div className="mono text-[9px] tracking-[0.15em] uppercase text-[var(--red)]">Day {d.day_number}</div>
            <div className="font-bold text-[13px] mt-0.5">{d.day_title || "Day title"}</div>
            <div className="text-[var(--ink3)] text-[11px]">{(d.blocks ?? []).filter((b) => b.activity_name).length}/3 blocks filled</div>
          </div>
        ))}
        {(itin.days ?? []).length > 1 && (
          <div className="text-[11px] text-[var(--ink3)] italic">+ {(itin.days ?? []).length - 1} day{(itin.days ?? []).length > 2 ? "s" : ""} locked behind buy box</div>
        )}
      </div>
    </div>
  );
}
